<template>
  <div class="container">
    <div class="mb-8 standard-list">
      <slot name="heading" />
      <p>
        Your next underwater adventure awaits. ZuBlu’s secure online booking,
        expert travel advisors and exclusive deals mean you can discover,
        compare and book scuba diving holidays with ease.
      </p>
      <p>
        Please enter your details and wishes below and a ZuBlu travel expert
        will be in touch shortly to assist you with your booking and answer any
        questions you may have.
      </p>
    </div>
    <div class="enquiry-form">
      <validation-observer tag="div" ref="valiObjserver">
        <form @submit.prevent="onBeforeSubmit">
          <input type="hidden" id="zc_gad" name="zc_gad" value="" />

          <label class="block mb-8">
            <p class="mb-2 fs-14">First name</p>
            <validation-provider
              ref="First Name"
              name="First Name"
              rules="required"
              v-slot="{ errors }"
            >
              <input
                class="input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model="firstName"
              />
              <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
            </validation-provider>
          </label>

          <label class="block mb-8">
            <p class="mb-2 fs-14">Last name</p>
            <validation-provider
              ref="Last Name"
              name="Last Name"
              rules="required"
              v-slot="{ errors }"
            >
              <input
                class="input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model="lastName"
              />
              <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
            </validation-provider>
          </label>

          <label class="block mb-8">
            <p class="mb-2 fs-14">Email</p>
            <validation-provider
              ref="Email"
              name="Email"
              rules="required|email"
              v-slot="{ errors }"
            >
              <input
                class="input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model.lazy="email"
              />
              <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
            </validation-provider>
          </label>

          <label class="block mb-8 w-4/5 sm:w-full">
            <p class="mb-2 fs-14">Phone</p>
            <validation-provider
              ref="Phone"
              name="Phone"
              rules="required"
              v-slot="{ errors }"
            >
              <phone-input
                class="input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model="phone"
              />

              <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
            </validation-provider>
          </label>

          <div class="mb-8">
            <label class="mb-2 fs-14 inline-block" for="inputDate"
              >Travel dates</label
            >
            <tooltip name="ttDates" ref="ttDates">
              <template v-slot:trigger="{ toggle, isShowing }">
                <div class="relative">
                  <validation-provider
                    ref="Dates"
                    name="Dates"
                    rules="required"
                    v-slot="{ errors }"
                    slim
                  >
                    <input
                      v-model="bookingInfo.date[1]"
                      class="h-0 block"
                      :class="{ 'input-booking-date-error': errors[0] }"
                    />
                  </validation-provider>

                  <div id="inputDate" class="flex items-center">
                    <svg
                      width="24"
                      height="24"
                      class="flex-none booking-widget-input-icon--left"
                    >
                      <use xlink:href="/img/sprite.svg#ic_calendar"></use>
                    </svg>
                    <input
                      type="text"
                      tabindex="100"
                      readonly
                      placeholder="Check-in — Check-out"
                      :value="dateDisplay"
                      class="cursor-pointer rounded input-tall w-full border border-black-30 booking-widget-input"
                      :class="{ 'force-focus': isShowing }"
                      @click="isMobile ? openMobileBookingWidget(1) : toggle()"
                    />
                  </div>

                  <p
                    id="inputDateMessage"
                    class="fs-14 text-danger mt-1 hidden"
                    v-if="!isShowing"
                  >
                    Please enter your booking dates
                  </p>
                </div>
              </template>

              <template>
                <div
                  class="px-5 pt-6 pb-6 bg-white rounded mt-2 md:rounded-none mdsm:p-0"
                >
                  <zb-daterangepicker
                    v-model="bookingInfo.date"
                  ></zb-daterangepicker>
                </div>
              </template>
            </tooltip>
          </div>

          <div class="mb-8 relative">
            <label for="inputGuests" class="mb-2 fs-14 inline-block"
              >Guests</label
            >
            <tooltip name="ttGuests" ref="ttGuests">
              <template v-slot:trigger="{ toggle, isShowing }">
                <div class="relative">
                  <validation-provider
                    ref="Guests"
                    name="Guests"
                    rules="min_value:1"
                    v-slot="{ errors }"
                    slim
                  >
                    <input
                      v-model="totalGuests"
                      class="h-0 block"
                      :class="{ 'input-booking-guests-error': errors[0] }"
                    />
                  </validation-provider>

                  <div id="inputGuests" class="flex items-center">
                    <input
                      type="text"
                      tabindex="101"
                      readonly
                      class="cursor-pointer rounded border border-black-30 input-tall w-full pl-5"
                      :class="{
                        'input--error': validations.moreRoomsThanGuests,
                        'force-focus': isShowing,
                      }"
                      placeholder="Add guests"
                      :value="guestsDisplay"
                      @click="isMobile ? openMobileBookingWidget(2) : toggle()"
                    />
                    <svg
                      width="24"
                      height="24"
                      class="flex-none booking-widget-input-icon--right pointer-events-none"
                      :class="{
                        'transform rotate-180 fill-secondary': isShowing,
                      }"
                    >
                      <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
                    </svg>
                  </div>

                  <p
                    id="inputGuestMessage"
                    class="fs-14 text-danger mt-1 hidden"
                    v-if="!isShowing"
                  >
                    Please enter your guest details
                  </p>
                </div>
              </template>

              <template>
                <div
                  class="px-5 pt-6 pb-6 bg-white rounded mt-2 left-0 right-0"
                >
                  <zb-guest-picker v-model="bookingInfo.guests" />
                </div>
              </template>
            </tooltip>
          </div>

          <div class="mb-8 relative">
            <label for="inputRooms" class="mb-2 fs-14 inline-block"
              >Rooms</label
            >
            <tooltip name="ttRooms" ref="ttRooms">
              <template v-slot:trigger="{ toggle, isShowing }">
                <div class="relative">
                  <validation-provider
                    ref="Rooms"
                    name="Rooms"
                    rules="min_value:1"
                    v-slot="{ errors }"
                    slim
                  >
                    <input
                      v-model="bookingInfo.rooms"
                      class="h-0 block"
                      :class="{ 'input-booking-rooms-error': errors[0] }"
                    />
                  </validation-provider>

                  <div id="inputRooms" class="flex items-center">
                    <input
                      type="text"
                      tabindex="102"
                      readonly
                      class="cursor-pointer rounded border border-black-30 input-tall w-full pl-5"
                      :class="{
                        'input--error': validations.moreRoomsThanGuests,
                        'force-focus': isShowing,
                      }"
                      placeholder="Add rooms"
                      :value="roomsDisplay"
                      @click="isMobile ? openMobileBookingWidget(3) : toggle()"
                    />
                    <svg
                      width="24"
                      height="24"
                      class="flex-none booking-widget-input-icon--right pointer-events-none"
                      :class="{
                        'transform rotate-180 fill-secondary': isShowing,
                      }"
                    >
                      <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
                    </svg>
                  </div>

                  <p
                    id="inputRoomMessage"
                    class="fs-14 text-danger mt-1 hidden"
                    v-if="!isShowing"
                  >
                    Please enter your room details
                  </p>
                </div>
              </template>

              <template>
                <div
                  class="px-5 pt-6 pb-6 bg-white rounded mt-2 left-0 right-0"
                >
                  <zb-room-picker v-model="bookingInfo.rooms" />
                </div>
              </template>
            </tooltip>
          </div>
          <p
            v-if="validations.moreRoomsThanGuests"
            class="text-danger fs-14 -mt-8 pt-1 mb-8"
          >
            You have selected more rooms than guests, please adjust your
            selection
          </p>

          <div class="mb-8">
            <label class="mb-2 fs-14 inline-block"
              >Budget per night (optional)</label
            >
            <custom-select
              v-model="budget"
              placeholder="Select your budget"
              :options="budgets"
            />
          </div>

          <div class="mb-8">
            <label class="mb-2 fs-14 inline-block"
              >Destination (optional)</label
            >
            <custom-select
              v-model="destination"
              placeholder="Select a destination"
              :options="destinations"
            />
          </div>

          <div class="mb-8">
            <label class="mb-2 fs-14 inline-block">Experience (optional)</label>
            <custom-select
              v-model="experience"
              placeholder="Select an experience"
              :options="experiences"
            />
          </div>

          <enquiry-direct-mobile-booking-widget
            v-model="bookingInfo"
            ref="mobileBookingWidget"
            class="mdsm:visible lg:hidden"
          >
          </enquiry-direct-mobile-booking-widget>

          <label>
            <p class="mb-2 fs-14">Special requests (Optional)</p>
            <textarea
              class="input w-full enquiry-additional-request"
              v-model="additionalMessage"
            />
          </label>

          <button
            class="mt-8 btn btn-primary input-tall font-medium sm:w-full sm:justify-center flex items-center"
            :disabled="isWorking"
            :class="isWorking ? 'loading' : ''"
          >
            <div class="flex justify-center">
              <loading-indicator width="20" class="mr-2" v-show="isWorking" />
              <span>Enquire</span>
            </div>
          </button>
        </form>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import {
  ValidationProvider,
  ValidationObserver,
  extend,
  setInteractionMode,
} from "vee-validate";
import { required, email, min_value } from "vee-validate/dist/rules";

import LoadingIndicator from "../../components/core/LoadingIndicator";
import Tooltip from "../../components/core/Tooltip";
import PhoneInput from "../../components/core/PhoneInput";
import DateRangerPickerNew from "../../components/core/DateRangePicker";
import GuestPicker from "../../components/core/Booking/DetailedGuestPicker";
import RoomPicker from "../../components/core/Booking/RoomPicker";
import EnquiryDirectMobileBookingWidget from "../../components/core/Booking/EnquiryDirectMobileBookingWidget";
import CustomSelect from "../../components/core/CustomSelect";
import {
  getBookingDateDisplay,
  getDetailedBookingGuestsDisplay,
  getBookingRoomsDisplay,
} from "../../helpers";

setInteractionMode("eager");
extend("email", email);
extend("min_value", min_value);
extend("required", {
  ...required,
  message: (field) => `Please enter your ${field}`,
});

const fieldsOrder = [
  "First Name",
  "Last Name",
  "Email",
  "Phone",
  "Dates",
  "Guests",
  "Rooms",
];

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    PhoneInput,
    CustomSelect,
    "zb-daterangepicker": DateRangerPickerNew,
    "zb-guest-picker": GuestPicker,
    "zb-room-picker": RoomPicker,
    EnquiryDirectMobileBookingWidget,
    Tooltip,
    LoadingIndicator,
  },
  name: "Enquiry",
  data() {
    return {
      isWorking: false,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      additionalMessage: "",
      bookingInfo: {
        date: [null, null],
        guests: {
          divers: 0,
          studentDivers: 0,
          nonDivers: 0,
        },
        rooms: 0,
      },
      budget: "",
      destination: "",
      experience: "",
      validations: {
        moreRoomsThanGuests: false,
      },
      formSubmitted: false,
    };
  },
  computed: {
    budgets() {
      return [
        { value: 1, label: "Less than 150 USD" },
        { value: 2, label: "Less than 250 USD" },
        { value: 3, label: "Less than 500 USD" },
        { value: 4, label: "More than 500 USD" },
      ];
    },
    destinations() {
      return [
        { label: "Australia", value: "AU" },
        { label: "Bahamas", value: "BH" },
        { label: "Belize", value: "BZ" },
        { label: "Cayman Islands", value: "CI" },
        { label: "Colombia", value: "CO" },
        { label: "Costa Rica", value: "CR" },
        { label: "Ecuador", value: "EC" },
        { label: "Fiji", value: "FJ" },
        { label: "French Polynesia", value: "PF" },
        { label: "Honduras", value: "HO" },
        { label: "Indonesia", value: "ID" },
        { label: "Japan", value: "JP" },
        { label: "Lesser Antilles", value: "LA" },
        { label: "Malaysia", value: "MY" },
        { label: "Maldives", value: "MV" },
        { label: "Mauritius", value: "MT" },
        { label: "Mexico", value: "MX" },
        { label: "Mozambique", value: "MZ" },
        { label: "Myanmar", value: "MM" },
        { label: "New Zealand", value: "NZ" },
        { label: "Pacific Islands", value: "PC" },
        { label: "Papua New Guinea", value: "PG" },
        { label: "Philippines", value: "PH" },
        { label: "Portugal", value: "PT" },
        { label: "Puerto Rico", value: "PR" },
        { label: "Red Sea", value: "RS" },
        { label: "Seychelles", value: "SC" },
        { label: "South Africa", value: "SA" },
        { label: "Sri Lanka", value: "LK" },
        { label: "Tanzania", value: "TZ" },
        { label: "Thailand", value: "TH" },
        { label: "Timor Leste", value: "TL" },
        { label: "Turks and Caicos", value: "TC" },
        { label: "Vietnam", value: "VN" },
      ];
    },
    experiences() {
      return [
        { value: 1, label: "Learn to dive" },
        { value: 2, label: "Sharks" },
        { value: 3, label: "Rays" },
        { value: 4, label: "Turtles" },
        { value: 5, label: "Schooling Fish" },
        { value: 6, label: "Macro Creatures" },
        { value: 7, label: "Reefs & Walls" },
        { value: 8, label: "Wrecks & Caves" },
        { value: 9, label: "Mammals & Pelagics" },
      ];
    },
    isMobile() {
      return window.matchMedia("only screen and (max-width: 1200px)").matches;
    },
    dateDisplay() {
      const [from, to] = this.bookingInfo.date;

      return getBookingDateDisplay(from, to).join(" — ");
    },
    guestsDisplay() {
      return getDetailedBookingGuestsDisplay(this.bookingInfo.guests);
    },
    roomsDisplay() {
      return getBookingRoomsDisplay(this.bookingInfo.rooms);
    },
    totalGuests: {
      // for vee-validate
      get() {
        return (
          this.bookingInfo.guests.divers +
          this.bookingInfo.guests.studentDivers +
          this.bookingInfo.guests.nonDivers
        );
      },
      set() {},
    },
  },
  mounted() {
    // https://zubludiving.atlassian.net/wiki/spaces/EN/pages/371654686/Widget+Integration
    const queries = new URLSearchParams(location.search);
    const fromDate = moment(queries.get("from_date") || "");
    const toDate = moment(queries.get("to_date") || "");
    const divers = Number(queries.get("num_divers"));
    const studentDivers = Number(queries.get("num_courses"));
    const nonDivers = Number(queries.get("num_non_divers"));
    const rooms = Number(queries.get("num_rooms"));

    if (fromDate.isValid() && toDate.isValid() && fromDate.isBefore(toDate)) {
      this.bookingInfo.date = [fromDate, toDate];
    }
    if (Number.isNaN(divers) === false) {
      this.bookingInfo.guests.divers = divers;
    }
    if (Number.isNaN(studentDivers) === false) {
      this.bookingInfo.guests.studentDivers = studentDivers;
    }
    if (Number.isNaN(nonDivers) === false) {
      this.bookingInfo.guests.nonDivers = nonDivers;
    }
    if (Number.isNaN(rooms) === false) {
      this.bookingInfo.rooms = rooms;
    }
  },
  created() {
    window.addEventListener("beforeunload", this.sendGoogleAnalytics);
  },
  methods: {
    sendGoogleAnalytics() {
      if (!this.formSubmitted) {
        window.pushDataLayer("zublu_cta", {
          cta_type: "enquiry",
          cta_value: "enquiry_leave",
          cta_screen: window.location.pathname,
        });
      }
    },
    openMobileBookingWidget(step) {
      this.$refs.mobileBookingWidget.show(step);
    },
    checkBeforeSubmit() {
      const [from, to] = this.bookingInfo.date;
      const { divers, studentDivers, nonDivers } = this.bookingInfo.guests;
      const totalGuests = divers + nonDivers + studentDivers;
      const rooms = this.bookingInfo.rooms;

      if (!from || !to) {
        if (this.isMobile) {
          this.openMobileBookingWidget(1);
        } else {
          this.$refs.ttDates.show();
        }

        return false;
      } else if (totalGuests === 0) {
        if (this.isMobile) {
          this.openMobileBookingWidget(2);
        } else {
          this.$refs.ttGuests.show();
        }

        return false;
      } else if (rooms === 0) {
        if (this.isMobile) {
          this.openMobileBookingWidget(3);
        } else {
          this.$refs.ttRooms.show();
        }

        return false;
      }

      if (totalGuests > 0 && rooms > 0 && rooms > totalGuests) {
        this.validations.moreRoomsThanGuests = true;
        return false;
      } else {
        this.validations.moreRoomsThanGuests = false;
        return true;
      }
    },
    onBeforeSubmit() {
      const valid = this.$refs.valiObjserver.validate().then((result) => {
        if (result === false) {
          setTimeout(() => {
            const errors = JSON.parse(
              JSON.stringify(this.$refs.valiObjserver.errors)
            );

            for (const field of fieldsOrder) {
              if (errors[field] && errors[field].length) {
                this.$refs[field].$el.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                break;
              }
            }
          }, 100); //https://github.com/logaretm/vee-validate/issues/2248
        } else {
          this.onSubmit();
        }
      });
    },
    onSubmit() {
      const canSubmit = this.checkBeforeSubmit();

      if (canSubmit) {
        this.formSubmitted = true;

        const payload = {
          destination_id: this.destination,
          first_name: this.firstName,
          last_name: this.lastName,
          email: this.email,
          phone: this.phone,
          check_in: this.bookingInfo.date[0].format("YYYY-MM-DD"),
          check_out: this.bookingInfo.date[1].format("YYYY-MM-DD"),
          number_of_guest: this.totalGuests,
          number_of_rooms: this.bookingInfo.rooms,
          enquiry_information: this.additionalMessage,
          url: window.location.href,
          session: window.Craft.session,
        };

        if (this.experience) {
          const item = this.experiences.find(
            (exp) => exp.value === this.experience
          );

          payload["interested_experience"] = [item.label];
        }

        if (this.budget) {
          const item = this.budgets.find((b) => b.value === this.budget);

          payload["budget_per_night"] = item.label;
        }

        const endpoint = window.ZUBLU_VARIABLES.ENQUIRY;

        this.isWorking = true;

        $.ajax({
          method: "POST",
          url: "/",
          data: {
            action: "zuBlu/form/submitEnquiry",
            [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
            formData: payload,
          },
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        })
          .done(() => {
            // sendGAEvent("Submit Enquiry","Contact Us Submit","");

            window.pushDataLayer("enquiry", {
              submit_location: "main_site",
              submit_type: "direct_destination",
            });

            const fullName = `${this.firstName} ${this.lastName}`;
            window.location.href = `/enquiry-success?user=${fullName}`;
          })
          .fail((e) => {
            window.pushDataLayer("zublu_cta", {
              cta_type: "enquiry",
              cta_value: "enquiry_error",
              cta_screen: window.location.pathname,
            });

            $.ajax({
              method: "POST",
              url:
                window.ZUBLU_VARIABLES.OCTOBOX_API +
                "/api/error-notification-enquiry",
              data: JSON.stringify({ enquiry: payload, err_message: e }),
              headers: {
                Authorization:
                  "Basic ZGV2ZWxvcG1lbnRAenVibHVkaXZpbmcuY29tOmRldjEyMzQ1",
                "Content-Type": "application/json",
              },
            })
              .done(() => {
                thankyoumessage.innerText = `Thank you for your booking enquiry, ${this.firstName}.`;
                form.classList.add("hidden");
                window.scrollTo(0, thankyou);
                thankyou.classList.remove("hidden");
              })
              .fail(console.error);
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
button {
  .zb-spin {
    display: none;
  }
  &.loading {
    .zb-spin {
      display: inline-block;
    }
  }
}

.standard-list ul li:before {
  color: #000;
}
</style>
